import { finalize } from 'rxjs/operators';
import { AccountService } from '@app/account/services/account.service';
import { LoginUserData } from '@app/account/account-api.model';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AccountState } from '@app/account/account-flow.model';

/**
 * Forgot Password component is the form to input an email to receive a link to reset
 * the password.
 * See reset-password.component for the page you reach after clicking a link in an
 * email to actually reset your password.
 */
@Component({
  selector: 'dgx-forgot-password-form',
  templateUrl: './forgot-password-form.component.html',
})
export class ForgotPasswordFormComponent implements OnInit {
  public readonly i18n = this.translateService.instant([
    'AccountController_Continue',
    'AccountController_Login',
    'forgotPasswordForm_Title',
    'forgotPasswordForm_Message',
    'forgotPasswordForm_EmailLabel',
    'forgotPasswordForm_EmailPlaceholder',
    'forgotPasswordForm_Success',
    'forgotPasswordForm_InvalidEmail',
    'forgotPasswordForm_SuccessMessage',
  ]);
  @Input() public specialInstructions =
    this.i18n.forgotPasswordForm_SuccessMessage;
  @Input() public passedData: Partial<LoginUserData>;
  @Input() public errors = '';

  @Output()
  public preserveReturnUrl: EventEmitter<any> = new EventEmitter<any>();
  @Output() public setUtm: EventEmitter<any> = new EventEmitter<any>();
  @Output() public goToState: EventEmitter<any> = new EventEmitter<any>();

  public user: Partial<LoginUserData> = {
    email: '',
  };
  public validation = { email: '' };
  public submitSuccess = false;
  public isSaving = false;

  constructor(
    private accountService: AccountService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    // prefer passed-in data
    this.user.email = this.passedData?.email || this.user.email;

    this.setUtm.emit();
    this.preserveReturnUrl.emit();
  }

  public submit($event, form) {
    $event.preventDefault();
    this.isSaving = true;

    if (this.checkEmail()) {
      this.validation.email = '';
      this.accountService
        .resetPassword(this.user.email)
        .pipe(
          finalize(() => {
            this.isSaving = false;
          })
        )
        .subscribe({
          next: () => {
            this.submitSuccess = true;
            this.cdr.detectChanges();
          },
          error: (error) => {
            this.isSaving = false;
            this.submitSuccess = false;
            this.errors = error;
            this.cdr.detectChanges();
          },
        });
    } else {
      this.validation.email = this.i18n.forgotPasswordForm_InvalidEmail;
      this.isSaving = false;
      this.focusField('username');
      this.cdr.detectChanges();
    }
  }

  public navigateToLogin() {
    this.goToState.emit({
      toState: AccountState.Login,
      passedData: {
        username: this.user.email,
      },
    });
  }

  public checkEmail() {
    return this.accountService.isValidEmail(this.user.email);
  }

  private focusField(elementId) {
    setTimeout(() => {
      const el = document.getElementById(elementId);
      el.focus();
    }, 0);
  }
}
